import { createHash } from "node:crypto";

export const CMP_CONSENT_STORAGE_VERSION = 1;
export const SITE_CMP_POLICY_VERSION = "cmp-v1";
export const WAITLIST_MARKETING_POLICY_VERSION = "waitlist-v1-2026-05";

export const CLIENT_CONSENT_CATEGORIES = [
	"necessary",
	"preferences",
	"analytics",
	"marketing",
] as const;

export type ClientConsentCategory = (typeof CLIENT_CONSENT_CATEGORIES)[number];

export const CONSENT_POLICY_CATEGORIES = [
	"NECESSARY",
	"PREFERENCES",
	"ANALYTICS",
	"MARKETING",
] as const;

export type ConsentPolicyCategory = (typeof CONSENT_POLICY_CATEGORIES)[number];

const CLIENT_TO_POLICY_CATEGORY: Record<
	ClientConsentCategory,
	ConsentPolicyCategory
> = {
	necessary: "NECESSARY",
	preferences: "PREFERENCES",
	analytics: "ANALYTICS",
	marketing: "MARKETING",
};

const POLICY_TO_CLIENT_CATEGORY: Record<
	ConsentPolicyCategory,
	ClientConsentCategory
> = {
	NECESSARY: "necessary",
	PREFERENCES: "preferences",
	ANALYTICS: "analytics",
	MARKETING: "marketing",
};

export function mapClientConsentCategoryToPolicy(
	category: ClientConsentCategory,
): ConsentPolicyCategory {
	return CLIENT_TO_POLICY_CATEGORY[category];
}

export function mapPolicyConsentCategoryToClient(
	category: ConsentPolicyCategory,
): ClientConsentCategory {
	return POLICY_TO_CLIENT_CATEGORY[category];
}

export interface ConsentIdempotencyKeyInput {
	sqid: string;
	domain: string;
	category: string;
	policyVersion: string;
	source: string;
	decidedAt: string;
	flow: string;
}

export function buildConsentIdempotencyKey(
	input: ConsentIdempotencyKeyInput,
): string {
	return createHash("sha256")
		.update(
			[
				input.sqid,
				input.domain,
				input.category,
				input.policyVersion,
				input.source,
				input.decidedAt,
				input.flow,
			].join("|"),
		)
		.digest("hex");
}

export interface ServerCmpConsentState {
	category: ConsentPolicyCategory;
	granted: boolean;
	decidedAt: Date | string;
}

export interface ClientConsentRecordShape {
	v: number;
	ts: number;
	cats: Record<ClientConsentCategory, boolean>;
}

export function buildClientConsentRecordFromServerStates(
	states: ServerCmpConsentState[],
	storageVersion: number,
): ClientConsentRecordShape {
	const cats: Record<ClientConsentCategory, boolean> = {
		necessary: true,
		preferences: false,
		analytics: false,
		marketing: false,
	};
	let latestDecidedAt = 0;

	for (const state of states) {
		const clientCategory = mapPolicyConsentCategoryToClient(state.category);
		if (clientCategory === "necessary") {
			continue;
		}
		cats[clientCategory] = state.granted;
		const decidedAt =
			state.decidedAt instanceof Date
				? state.decidedAt.getTime()
				: Date.parse(state.decidedAt);
		if (Number.isFinite(decidedAt)) {
			latestDecidedAt = Math.max(latestDecidedAt, decidedAt);
		}
	}

	return {
		v: storageVersion,
		ts: latestDecidedAt > 0 ? latestDecidedAt : Date.now(),
		cats,
	};
}

export function latestServerCmpDecidedAt(
	states: ServerCmpConsentState[],
): number {
	let latestDecidedAt = 0;
	for (const state of states) {
		const decidedAt =
			state.decidedAt instanceof Date
				? state.decidedAt.getTime()
				: Date.parse(state.decidedAt);
		if (Number.isFinite(decidedAt)) {
			latestDecidedAt = Math.max(latestDecidedAt, decidedAt);
		}
	}
	return latestDecidedAt;
}
